import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as profilesApi from "./profiles-api";
import { profileQueries } from "./profiles-keys";
import type { Profile } from "@shared/types/supabase.types";

/**
 * Hook to fetch a user's profile
 * Uses TanStack Query with type-safe query key factory
 */
export function useProfileQuery(userId: string, enabled = true) {
  return useQuery({
    ...profileQueries.byUser(userId),
    enabled: enabled && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes - profiles don't change frequently
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to upsert a user profile
 * Implements optimistic updates with automatic cache management
 */
export function useUpsertProfileMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: profilesApi.upsertProfile,
    onMutate: async (updatedProfile) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: profileQueries.byUser(updatedProfile.user_id).queryKey,
      });

      // Snapshot the previous value for rollback
      const previousProfile = queryClient.getQueryData<Profile>(
        profileQueries.byUser(updatedProfile.user_id).queryKey,
      );

      // Optimistically update cache
      queryClient.setQueryData<Profile>(
        profileQueries.byUser(updatedProfile.user_id).queryKey,
        (old) =>
          old
            ? {
                ...old,
                ...updatedProfile,
                updated_at: new Date().toISOString(),
              }
            : undefined,
      );

      return { previousProfile, updatedProfile };
    },
    onError: (error, updatedProfile, context) => {
      // Rollback optimistic update on error
      if (context?.previousProfile) {
        queryClient.setQueryData<Profile>(
          profileQueries.byUser(updatedProfile.user_id).queryKey,
          context.previousProfile,
        );
      }
      console.error("Failed to update profile:", error);
    },
    onSuccess: (data, updatedProfile) => {
      // Update cache with server response
      queryClient.setQueryData<Profile>(
        profileQueries.byUser(updatedProfile.user_id).queryKey,
        data,
      );
      console.log("Profile updated successfully");
    },
    onSettled: (_data, _error, updatedProfile) => {
      // Ensure eventual consistency
      void queryClient.invalidateQueries({
        queryKey: profileQueries.byUser(updatedProfile.user_id).queryKey,
      });
    },
  });
}

/**
 * Hook to update a user's avatar URL
 * Convenience wrapper around upsert mutation specifically for avatar updates
 */
export function useUpdateAvatarUrlMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: profilesApi.updateAvatarUrl,
    onMutate: async ({ userId, avatarUrl }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: profileQueries.byUser(userId).queryKey,
      });

      // Snapshot the previous value for rollback
      const previousProfile = queryClient.getQueryData<Profile>(
        profileQueries.byUser(userId).queryKey,
      );

      // Optimistically update cache
      queryClient.setQueryData<Profile>(profileQueries.byUser(userId).queryKey, (old) =>
        old
          ? {
              ...old,
              avatar_url: avatarUrl,
              updated_at: new Date().toISOString(),
            }
          : undefined,
      );

      return { previousProfile };
    },
    onError: (error, { userId }, context) => {
      // Rollback optimistic update on error
      if (context?.previousProfile) {
        queryClient.setQueryData<Profile>(
          profileQueries.byUser(userId).queryKey,
          context.previousProfile,
        );
      }
      console.error("Failed to update avatar URL:", error);
    },
    onSuccess: (data, { userId }) => {
      // Update cache with server response
      queryClient.setQueryData<Profile>(profileQueries.byUser(userId).queryKey, data);
      console.log("Avatar URL updated successfully");
    },
    onSettled: (_data, _error, { userId }) => {
      // Always invalidate to ensure consistency with server
      void queryClient.invalidateQueries({
        queryKey: profileQueries.byUser(userId).queryKey,
      });
    },
  });
}

/**
 * Hook to set up real-time profile subscription
 * Automatically updates cache when profile is updated
 */
export function useProfileSubscription(userId: string, enabled = true) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: profileQueries.subscription(userId).queryKey,
    queryFn: () => {
      if (!enabled || !userId) return null;

      const channel = profilesApi.createProfileSubscription(userId, (payload) => {
        // Invalidate and refetch profile for this user
        void queryClient.invalidateQueries({
          queryKey: profileQueries.byUser(userId).queryKey,
        });

        // For UPDATE events, we can also do optimistic updates
        if (payload.eventType === "UPDATE") {
          queryClient.setQueryData<Profile>(
            profileQueries.byUser(userId).queryKey,
            payload.new as Profile,
          );
        }
      });

      return channel;
    },
    enabled: enabled && !!userId,
    staleTime: Infinity, // Never stale - it's managing a subscription
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
